import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@tsuyosa/auth';
import { getChekiOrderStatus, type OrderStatusData } from '../lib/api';
import { CountdownTimer } from '../components/CountdownTimer';

const STATUS_LABELS: Record<string, string> = {
  pending_payment:      'Menunggu Bukti Bayar',
  waiting_verification: 'Sedang Diverifikasi',
  paid:                 'Pembayaran Dikonfirmasi ✓',
  done:                 'Selesai ✓',
  cancelled:            'Dibatalkan',
  pending:              'Pending',
};

const STATUS_COLORS: Record<string, string> = {
  pending_payment:      'text-blue-400 bg-blue-400/10',
  waiting_verification: 'text-amber-400 bg-amber-400/10',
  paid:                 'text-green-400 bg-green-400/10',
  done:                 'text-green-400 bg-green-400/10',
  cancelled:            'text-red-400 bg-red-400/10',
  pending:              'text-white/40 bg-white/5',
};

const STATUS_MESSAGES: Record<string, string> = {
  pending_payment:      'Selesaikan pembayaran dan upload bukti sebelum waktu habis.',
  waiting_verification: 'Bukti bayar sudah diterima. Admin sedang memverifikasi...',
  paid:                 'Pembayaranmu telah dikonfirmasi. Sampai jumpa di event!',
  done:                 'Pesanan selesai.',
  cancelled:            'Pesanan ini dibatalkan. Hubungi admin untuk info lebih lanjut.',
};

function formatDate(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}
function formatPrice(n: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', maximumFractionDigits: 0,
  }).format(n);
}

export function OrderStatusPage() {
  const { id }          = useParams<{ id: string }>();
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get('token') ?? undefined;
  const { accessToken } = useAuth();

  const [order, setOrder]         = useState<OrderStatusData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState('');
  const [proofOpen, setProofOpen] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!id) return;
    setError('');
    try {
      const data = await getChekiOrderStatus(id, token, accessToken);
      setOrder(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [id, token, accessToken]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span
          className="h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
          style={{ borderColor: 'var(--pink)' }}
        />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center gap-3">
        <p className="text-white/40">{error || 'Pesanan tidak ditemukan.'}</p>
        <a href="/" className="text-sm" style={{ color: 'var(--pink)' }}>Kembali</a>
      </div>
    );
  }

  const deadline = order.payment_deadline_at ? new Date(order.payment_deadline_at) : null;

  return (
    <div className="min-h-screen px-4 py-10">
      <div className="mx-auto max-w-sm">
        <p className="mb-1 text-[10px] uppercase tracking-[0.4em] text-white/30">TSUYOSA</p>
        <h1 className="mb-6 text-xl font-bold text-white">Status Pesanan</h1>

        <div
          className={`mb-4 inline-flex rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[order.status] ?? 'text-white/40 bg-white/5'}`}
        >
          {STATUS_LABELS[order.status] ?? order.status}
        </div>

        {STATUS_MESSAGES[order.status] && (
          <p className="mb-5 text-sm text-white/50">{STATUS_MESSAGES[order.status]}</p>
        )}

        {order.status === 'pending_payment' && deadline && (
          <div
            className="mb-5 flex items-center gap-2 rounded-xl border px-4 py-3"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="text-xs text-white/40">Sisa waktu:</span>
            <CountdownTimer
              deadlineAt={deadline}
              onExpired={() => setOrder(prev => prev ? { ...prev, status: 'cancelled' } : prev)}
            />
          </div>
        )}

        <div
          className="rounded-xl border p-5 flex flex-col gap-3"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          {order.schedule && (
            <div>
              <p className="text-xs text-white/40">Jadwal</p>
              <p className="font-semibold text-white">{order.schedule.title}</p>
              <p className="text-sm text-white/50">{formatDate(order.schedule.date)}</p>
            </div>
          )}

          <div className="border-t" style={{ borderColor: 'var(--border)' }} />

          <div className="flex flex-col gap-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-white/40">Tipe</span>
              <span className="text-white">{order.type === '2shot_solo' ? '2-Shot Solo' : 'All Member'}</span>
            </div>
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between">
                <span className="text-white/40">{item.nickname ?? item.member_id}</span>
                <span className="text-white">×{item.quantity}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold">
              <span className="text-white/40">Total</span>
              <span className="text-white">{formatPrice(order.total_price)}</span>
            </div>
          </div>

          {order.payment_proof_url && (
            <>
              <div className="border-t" style={{ borderColor: 'var(--border)' }} />
              <div>
                <p className="mb-2 text-xs text-white/40">Bukti Bayar</p>
                <img
                  src={order.payment_proof_url}
                  alt="Bukti bayar"
                  className="h-24 w-full cursor-pointer rounded-lg object-cover"
                  onClick={() => setProofOpen(true)}
                />
              </div>
            </>
          )}
        </div>

        <p className="mt-3 font-mono text-[9px] text-white/20">{order.id}</p>

        <button
          onClick={fetchOrder}
          className="mt-3 text-xs text-white/30 underline hover:text-white/60 transition"
        >
          Refresh status
        </button>
      </div>

      {proofOpen && order.payment_proof_url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setProofOpen(false)}
        >
          <img
            src={order.payment_proof_url}
            alt="Bukti bayar"
            className="max-h-full max-w-full rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
